import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/config';
import './App.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import analyticsService from './utils/analyticsService';

// Import components (kept eager — small, needed on every page)
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import MobileBottomNav from './components/common/MobileBottomNav';
import ScrollToTop from './components/common/ScrollToTop';
import AdminRoute from './components/auth/AdminRoute';
import WhatsAppWidget from './components/common/WhatsAppWidget';
import { AlertProvider } from './context/AlertContext';
import { UserProvider } from './context/UserContext';
import { LocationProvider } from './context/LocationContext';
import PageViewTracker from './components/analytics/PageViewTracker';

// Lazy-loaded pages — each becomes a separate JS chunk
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const VetConsultation = lazy(() => import('./pages/VetConsultation'));
const AIVet = lazy(() => import('./pages/AIVet'));
const Contact = lazy(() => import('./pages/Contact'));
const Careers = lazy(() => import('./pages/Careers'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const ReturnPolicy = lazy(() => import('./pages/ReturnPolicy'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const DoctorBooking = lazy(() => import('./components/booking/DoctorBooking'));
const UserOrders = lazy(() => import('./pages/UserOrders'));
const UserBookings = lazy(() => import('./pages/UserBookings'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const MobileProfile = lazy(() => import('./pages/MobileProfile'));
const NotFound = lazy(() => import('./pages/NotFound'));
const PetTransportation = lazy(() => import('./pages/PetTransportation'));
const PetRehoming = lazy(() => import('./pages/PetRehoming'));
const FindFurrySoulmate = lazy(() => import('./pages/FindFurrySoulmate'));
const PetDetails = lazy(() => import('./pages/PetDetails'));
const PetBoarding = lazy(() => import('./pages/PetBoarding'));
const BoardingDetail = lazy(() => import('./pages/BoardingDetail'));
const PetBoardingAdmin = lazy(() => import('./pages/admin/PetBoardingAdmin'));
const PetGrooming = lazy(() => import('./pages/PetGrooming'));
const GroomingDashboard = lazy(() => import('./pages/GroomingDashboard'));
const GroomingDetail = lazy(() => import('./pages/GroomingDetail'));
const GroomingBooking = lazy(() => import('./pages/GroomingBooking'));
const BookingConfirmation = lazy(() => import('./pages/BookingConfirmation'));
const GroomingAdmin = lazy(() => import('./pages/admin/GroomingAdmin'));
const GroomingAdminDashboard = lazy(() => import('./pages/admin/GroomingAdminDashboard'));
const MyPets = lazy(() => import('./pages/MyPets'));
const AIVetChat = lazy(() => import('./pages/AIVetChat'));
const BoardingAdminDashboard = lazy(() => import('./components/admin/boarding/BoardingAdminDashboard'));
const AppDownloadComing = lazy(() => import('./components/common/AppDownloadComing'));

// Diet Plan Service Components
const DietPlanForm = lazy(() => import('./components/services/DietPlanForm'));
const DietPlanPayment = lazy(() => import('./components/services/DietPlanPayment'));
const DietPlanSuccess = lazy(() => import('./components/services/DietPlanSuccess'));
const DietPlanAdmin = lazy(() => import('./components/admin/DietPlanAdmin'));

// TailTalks Community pages
const TailTalks = lazy(() => import('./pages/TailTalks'));
const CommunityDetail = lazy(() => import('./pages/CommunityDetail'));
const CreateCommunity = lazy(() => import('./pages/CreateCommunity'));
const CreatePost = lazy(() => import('./pages/CreatePost'));
const PostDetail = lazy(() => import('./pages/PostDetail'));
const TailTalksPostDetail = lazy(() => import('./pages/TailTalksPostDetail'));
const TailTalksAdmin = lazy(() => import('./pages/admin/TailTalksAdmin'));
const MyPosts = lazy(() => import('./pages/MyPosts'));
const TailTalksManagementDebug = lazy(() => import('./pages/admin/TailTalksManagementDebug'));

// Store components
const StoreLogin = lazy(() => import('./pages/StoreLogin'));
const StoreDashboard = lazy(() => import('./pages/StoreDashboard'));

// Layout component to conditionally render Navbar and Footer
const Layout = ({ children }) => {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');
  const isStorePage = location.pathname.startsWith('/store');
  
  return (
    <div className="App min-h-screen bg-secondary-light text-primary-dark flex flex-col">
      {!isAdminPage && !isStorePage && <Navbar />}
      <main className={`flex-grow ${!isAdminPage && !isStorePage ? 'pt-[120px] md:pt-28 pb-16 md:pb-0' : ''}`}>
        {children}
      </main>
      {!isAdminPage && !isStorePage && (
        <>
          <Footer />
          <MobileBottomNav />
          <WhatsAppWidget />
        </>
      )}
    </div>
  );
};

// Wrapper component to pass admin data to BoardingAdminDashboard
const BoardingAdminWrapper = () => {
  const adminAuth = localStorage.getItem('adminAuth');
  const adminData = adminAuth ? JSON.parse(adminAuth) : null;
  
  console.log('Admin Data Structure:', adminData);
  
  if (!adminData) {
    return <Navigate to="/admin/login" />;
  }
  
  return <BoardingAdminDashboard adminData={adminData} />;
};

function App() {
  useEffect(() => {
    console.log('App component mounted');
  }, []);

  return (
    <AlertProvider>
      <UserProvider>
        <LocationProvider>
          <Router>
          <ScrollToTop />
          <PageViewTracker />
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
          <Routes>
            {/* Directly land on Home */}
            <Route path="/" element={<Layout><Home /></Layout>} />
            
            {/* Main Website Routes */}
            <Route path="/home" element={<Layout><Home /></Layout>} />
            <Route path="/about" element={<Layout><About /></Layout>} />
            <Route path="/services" element={<Layout><Services /></Layout>} />
            <Route path="/blog" element={<Layout><Blog /></Layout>} />
            <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />
            <Route path="/contact" element={<Layout><Contact /></Layout>} />
            <Route path="/careers" element={<Layout><Careers /></Layout>} />
            <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
            <Route path="/terms-and-conditions" element={<Layout><TermsAndConditions /></Layout>} />
            <Route path="/return-policy" element={<Layout><ReturnPolicy /></Layout>} />
            <Route path="/products" element={<Layout><Products /></Layout>} />
            <Route path="/products/:productId" element={<Layout><ProductDetail /></Layout>} />
            <Route path="/cart" element={<Layout><Cart /></Layout>} />
            <Route path="/online-vet-consultation" element={<Layout><VetConsultation /></Layout>} />
            <Route path="/ai-vet" element={<Layout><AIVet /></Layout>} />
            <Route path="/book-appointment" element={<Layout><DoctorBooking /></Layout>} />
            <Route path="/my-orders" element={<Layout><UserOrders /></Layout>} />
            <Route path="/my-bookings" element={<Layout><UserBookings /></Layout>} />
            <Route path="/my-pets" element={<Layout><MyPets /></Layout>} />
            <Route path="/pawdoc-ai" element={<AIVetChat />} />
            <Route path="/profile" element={<Layout>
              <div className="md:block hidden"><UserProfile /></div>
              <div className="md:hidden block"><MobileProfile /></div>
            </Layout>} />
            <Route path="/admin" element={<Layout><AdminLogin /></Layout>} />

            {/* Admin Dashboard Route */}
            <Route path="/admin/dashboard" element={
              <Layout>
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              </Layout>
            } />

            <Route path="/admin/tailtalk" element={<Layout><AdminRoute><TailTalksAdmin /></AdminRoute></Layout>} />

            <Route path="/admin/tailtalk-management" element={<Layout><AdminRoute><TailTalksAdmin /></AdminRoute></Layout>} />

            <Route path="/admin/dashboard/tailtalk-management" element={<Layout><AdminRoute><TailTalksAdmin /></AdminRoute></Layout>} />

            <Route path="/admin/pet-boarding" element={
              <Layout>
                <AdminRoute>
                  <PetBoardingAdmin />
                </AdminRoute>
              </Layout>
            } />

            <Route path="/admin/grooming" element={
              <Layout>
                <AdminRoute>
                  <GroomingAdmin />
                </AdminRoute>
              </Layout>
            } />

            <Route path="/admin/grooming-dashboard" element={
              <Layout>
                <AdminRoute>
                  <GroomingAdminDashboard />
                </AdminRoute>
              </Layout>
            } />

            <Route path="/admin/diet-plan" element={
              <Layout>
                <AdminRoute>
                  <DietPlanAdmin />
                </AdminRoute>
              </Layout>
            } />

            <Route path="/admin/boarding-dashboard" element={
              <Layout>
                <AdminRoute>
                  <BoardingAdminWrapper />
                </AdminRoute>
              </Layout>
            } />

            <Route path="/admin/pet-parenting/:id" element={<Layout><PetDetails /></Layout>} />

            {/* Store Routes */}
            <Route path="/store/login" element={<StoreLogin />} />
            <Route path="/store/dashboard" element={<StoreDashboard />} />

            <Route path="/download/ios" element={<Layout><AppDownloadComing storeType="ios" /></Layout>} />
            <Route path="/download/android" element={<Layout><AppDownloadComing storeType="android" /></Layout>} />
            <Route path="/services/transportation" element={<Layout><PetTransportation /></Layout>} />
            <Route path="/services/boarding" element={<Layout><PetBoarding /></Layout>} />
            <Route path="/services/boarding/:id" element={<Layout><BoardingDetail /></Layout>} />
            <Route path="/services/grooming" element={<Layout><PetGrooming /></Layout>} />
            <Route path="/services/grooming/dashboard" element={<Layout><GroomingDashboard /></Layout>} />
            <Route path="/services/grooming/:id" element={<Layout><GroomingDetail /></Layout>} />
            <Route path="/services/grooming/:id/booking" element={<Layout><GroomingBooking /></Layout>} />
            <Route path="/booking-confirmation/:id" element={<Layout><BookingConfirmation /></Layout>} />
            
            {/* Diet Plan Service Routes */}
            <Route path="/services/diet-plan" element={<Layout><DietPlanForm /></Layout>} />
            <Route path="/services/diet-plan/payment" element={<Layout><DietPlanPayment /></Layout>} />
            <Route path="/services/diet-plan/success" element={<Layout><DietPlanSuccess /></Layout>} />
            
            <Route path="/rehome" element={<Layout><PetRehoming /></Layout>} />
            <Route path="/find-furry-soulmate" element={<Layout><FindFurrySoulmate /></Layout>} />
            <Route path="/pets/:petId" element={<Layout><PetDetails /></Layout>} />
            
            {/* TailTalks Community Routes */}
            <Route path="/tailtalk" element={<Layout><TailTalks /></Layout>} />
            <Route path="/tailtalk/post/:postId" element={<Layout><TailTalksPostDetail /></Layout>} />
            <Route path="/tailtalk/myposts" element={<Layout><MyPosts /></Layout>} />
            
            {/* Debug Routes */}
            <Route path="/admin/tailtalk-debug" element={<Layout><TailTalksManagementDebug /></Layout>} />
            
            {/* Legacy TailTalks Routes - Will be deprecated */}
            <Route path="/tailtalk/community/:communityId" element={<Layout><CommunityDetail /></Layout>} />
            <Route path="/tailtalk/create-community" element={<Layout><CreateCommunity /></Layout>} />
            <Route path="/tailtalk/community/:communityId/create-post" element={<Layout><CreatePost /></Layout>} />
            <Route path="/tailtalk/community/:communityId/post/:postId" element={<Layout><PostDetail /></Layout>} />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </Router>
      </LocationProvider>
    </UserProvider>
  </AlertProvider>
  );
}

export default App; 